import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  getBestNiches,
  triggerBestNiches,
  getBestNichesScan,
} from "@/lib/api";

const FACTOR_LABELS = {
  proof_of_money: "Proof of $",
  multiple_winners: "Multiple winners",
  format_simplicity: "Format simplicity",
  cadence_realism: "Realistic cadence",
  equipment_free: "Equipment-free",
  recency: "Still hot",
};

function nFmt(n) {
  if (n == null || isNaN(n)) return "—";
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(n >= 10_000 ? 0 : 1)}k`;
  return String(n);
}

function scoreColor(s) {
  if (s == null) return "text-zinc-400";
  if (s >= 80) return "text-emerald-300";
  if (s >= 65) return "text-yellow-400";
  if (s >= 50) return "text-amber-400";
  return "text-zinc-400";
}

export default function NicheOpportunities() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [stage, setStage] = useState(null);
  const [elapsed, setElapsed] = useState(0);
  const [openIdx, setOpenIdx] = useState(0);
  const pollRef = useRef(null);
  const elapsedRef = useRef(null);

  useEffect(() => () => {
    clearInterval(pollRef.current);
    clearInterval(elapsedRef.current);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await getBestNiches();
      setData(d);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't load");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const runScan = async (force = false) => {
    setScanning(true);
    setStage("starting");
    setElapsed(0);
    elapsedRef.current = setInterval(() => setElapsed((e) => e + 1), 1000);
    try {
      const { scan_id, status, from_cache } = await triggerBestNiches(force);
      if (from_cache && status === "complete") {
        toast.success("Loaded latest cached ranking");
        clearInterval(elapsedRef.current);
        await load();
        setScanning(false);
        return;
      }
      toast.info("Starting AI ranking — could take ~60s if we need to scan YouTube first");
      clearInterval(pollRef.current);
      pollRef.current = setInterval(async () => {
        try {
          const doc = await getBestNichesScan(scan_id);
          if (doc.stage) setStage(doc.stage);
          if (doc.status === "complete") {
            clearInterval(pollRef.current);
            clearInterval(elapsedRef.current);
            setScanning(false);
            await load();
            setOpenIdx(0);
            toast.success(`Ranked ${doc.cluster_count || doc.clusters?.length} niche opportunities`);
          } else if (doc.status === "failed") {
            clearInterval(pollRef.current);
            clearInterval(elapsedRef.current);
            setScanning(false);
            toast.error(doc.error || "Ranking failed");
          }
        } catch {
          clearInterval(pollRef.current);
          clearInterval(elapsedRef.current);
          setScanning(false);
          toast.error("Lost connection");
        }
      }, 3500);
    } catch (err) {
      clearInterval(elapsedRef.current);
      setScanning(false);
      toast.error(err?.response?.data?.detail || "Couldn't start ranking");
    }
  };

  const cancelScan = () => {
    clearInterval(pollRef.current);
    clearInterval(elapsedRef.current);
    setScanning(false);
    setStage(null);
    toast.info("Cancelled");
  };

  const clusters = data?.clusters || [];
  const hasResults = clusters.length > 0;
  const stale = data?.stale;
  const age = data?.age_hours;

  return (
    <div data-testid="niche-opportunities-page" className="px-6 lg:px-10 py-8 max-w-[1300px] mx-auto">
      <div className="font-mono text-xs uppercase tracking-[0.3em] text-yellow-400 mb-3">
        // PHASE 1 · NICHE OPPORTUNITIES
      </div>
      <h1 className="font-display text-4xl sm:text-5xl font-black text-white mb-3 leading-none">
        The best niches to launch right now.
      </h1>
      <p className="text-zinc-300 text-sm max-w-2xl mb-6 leading-relaxed">
        AI ranks niches where channels are already making real money and the
        format is reproducible. Each card has a <b>Niche Playbook</b> —
        exactly what to film in week 1, what to lean into, what to
        differentiate, and honest replication risk.
      </p>

      <div className="flex flex-wrap items-center gap-3 mb-10">
        <button
          data-testid="bn-scan-btn"
          onClick={() => runScan(false)}
          disabled={scanning}
          className="bg-yellow-400 text-yellow-950 font-bold px-6 py-3 rounded-sm uppercase tracking-wide text-sm hover:bg-yellow-300 transition-colors disabled:opacity-60"
        >
          {scanning ? "AI ranking…" : hasResults ? "▸ Refresh ranking" : "▸ Rank niches"}
        </button>
        {hasResults && (
          <button
            data-testid="bn-force-btn"
            onClick={() => runScan(true)}
            disabled={scanning}
            className="font-mono text-xs uppercase tracking-[0.25em] text-zinc-300 hover:text-yellow-400 border border-zinc-800 hover:border-yellow-400 px-4 py-3 rounded-sm transition-colors disabled:opacity-60"
          >
            ↻ Re-rank
          </button>
        )}
        {hasResults && age != null && (
          <span className={`font-mono text-[11px] uppercase tracking-[0.25em] ${stale ? "text-amber-400" : "text-zinc-500"}`}>
            cached {age < 1 ? `${Math.round(age * 60)}m` : `${age}h`} ago{stale ? " · stale" : ""}
          </span>
        )}
      </div>

      {loading ? (
        <div className="font-mono text-sm text-zinc-500">[recon] loading<span className="blink">_</span></div>
      ) : scanning ? (
        <div className="border border-zinc-800 rounded-sm p-6" data-testid="bn-progress">
          <div className="font-mono text-xs uppercase tracking-[0.3em] text-yellow-400 mb-3">
            // working · {elapsed}s elapsed
          </div>
          <div className="space-y-2 mb-4">
            <StageRow label="Scanning YouTube for top earners" active={stage === "scanning_youtube" || stage === null} done={stage === "ranking_with_ai"} />
            <StageRow label="AI ranking by replicability" active={stage === "ranking_with_ai"} done={false} />
          </div>
          <div className="text-xs text-zinc-300 leading-relaxed mb-4">
            {stage === "scanning_youtube" || stage === null
              ? "Scanning YouTube for small/new channels earning real revenue. This takes ~60s on first run."
              : "Claude is grouping channels into clusters and writing each niche playbook. ~30s more."}
          </div>
          {elapsed > 30 && (
            <button
              onClick={cancelScan}
              data-testid="bn-cancel-btn"
              className="font-mono text-xs uppercase tracking-[0.25em] text-zinc-300 hover:text-yellow-400 border border-zinc-700 hover:border-yellow-400 px-4 py-2 rounded-sm transition-colors"
            >
              ✕ Cancel
            </button>
          )}
        </div>
      ) : !hasResults ? (
        <div className="border border-zinc-800 rounded-sm p-10 text-center" data-testid="bn-empty">
          <div className="font-mono text-xs uppercase tracking-[0.3em] text-yellow-400 mb-3">// NO RANKING YET</div>
          <p className="text-zinc-300 text-sm mb-2">
            Click <b>Rank niches</b> above. We'll take the latest Top Earners
            scan and have AI cluster them into the most replicable opportunities.
          </p>
          <p className="text-zinc-500 text-xs">
            (If you haven't run a Top Earners scan yet, do that first.)
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {clusters.map((c, i) => (
            <ClusterCard
              key={i}
              cluster={c}
              index={i}
              open={openIdx === i}
              onToggle={() => setOpenIdx(openIdx === i ? -1 : i)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function ClusterCard({ cluster, index, open, onToggle }) {
  const score = cluster.replication_score || 0;
  const isHero = index === 0;
  return (
    <div
      data-testid={`bn-cluster-${index}`}
      className={`rounded-sm border ${isHero ? "border-yellow-400/40 bg-yellow-400/[0.03]" : "border-zinc-800 bg-zinc-950/40"}`}
    >
      <button
        onClick={onToggle}
        className="w-full text-left px-5 py-4 flex items-start gap-4"
        data-testid={`bn-cluster-toggle-${index}`}
      >
        <div className={`text-center shrink-0 w-16 ${isHero ? "" : ""}`}>
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">Rank</div>
          <div className={`font-display text-3xl font-black ${isHero ? "text-yellow-400" : "text-white"}`}>
            {String(index + 1).padStart(2, "0")}
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline gap-3 flex-wrap mb-1">
            <h2 className={`font-display text-xl sm:text-2xl font-black ${isHero ? "text-yellow-300" : "text-white"}`}>
              {cluster.name}
            </h2>
            {isHero && (
              <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-yellow-400 bg-yellow-400/10 px-2 py-0.5 rounded-sm border border-yellow-400/30">
                Top pick
              </span>
            )}
          </div>
          <p className="text-zinc-300 text-sm">{cluster.summary}</p>
        </div>
        <div className="text-right shrink-0">
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">Replication</div>
          <div className={`font-display text-3xl font-black ${scoreColor(score)}`}>
            {score}
          </div>
        </div>
      </button>

      {open && (
        <div className="border-t border-zinc-800 px-5 py-5 space-y-5">
          {/* Factor scores */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
            {Object.entries(FACTOR_LABELS).map(([k, label]) => {
              const v = cluster.factor_scores?.[k] ?? 0;
              return (
                <div key={k} className="border border-zinc-800 p-2 rounded-sm">
                  <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-zinc-500">{label}</div>
                  <div className={`font-mono text-lg font-bold ${scoreColor(v)}`}>{v}</div>
                  <div className="h-1 bg-zinc-900 mt-1">
                    <div className="h-full bg-yellow-400" style={{ width: `${Math.min(100, v)}%` }} />
                  </div>
                </div>
              );
            })}
          </div>

          {/* Proof channels */}
          {cluster.proof_channels?.length > 0 && (
            <Section title="Proof — channels already winning this">
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {cluster.proof_channels.map((p, j) => (
                  <a
                    key={j}
                    href={p.url}
                    target="_blank"
                    rel="noreferrer"
                    className="border border-zinc-800 hover:border-yellow-400 transition-colors p-3 rounded-sm"
                  >
                    <div className="text-sm text-white font-semibold truncate">{p.name}</div>
                    <div className="flex items-baseline gap-3 mt-1">
                      <span className="font-display text-lg font-bold text-emerald-300">
                        ${nFmt(p.rev_per_month_usd)}<span className="text-zinc-500 text-[10px] font-normal"> / mo</span>
                      </span>
                      <span className="font-mono text-[11px] text-zinc-400">{nFmt(p.subs)} subs</span>
                    </div>
                  </a>
                ))}
              </div>
            </Section>
          )}

          {/* Formula */}
          {cluster.formula && (
            <Section title="The formula">
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-3">
                <Stat label="Format" value={cluster.formula.format?.replace("_", " ") || "—"} />
                <Stat label="Typical length" value={
                  cluster.formula.typical_length_sec
                    ? `${cluster.formula.typical_length_sec < 60
                        ? cluster.formula.typical_length_sec + "s"
                        : Math.round(cluster.formula.typical_length_sec / 60) + " min"}`
                    : "—"
                } />
                <Stat label="Uploads / week" value={cluster.formula.uploads_per_week ?? "—"} />
                <Stat label="Production" value={cluster.formula.production || "—"} small />
              </div>
              {cluster.formula.opening_hook_style && (
                <div className="text-sm text-zinc-200 leading-relaxed">
                  <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-yellow-400 mr-2">Hook style:</span>
                  {cluster.formula.opening_hook_style}
                </div>
              )}
            </Section>
          )}

          {/* First week shot list */}
          {cluster.first_week_shot_list?.length > 0 && (
            <Section title="Your first week — 5 videos to film">
              <ol className="space-y-2">
                {cluster.first_week_shot_list.map((s, j) => (
                  <li key={j} className="flex gap-3 items-start text-sm text-zinc-200 border-l-2 border-zinc-800 pl-3 py-1 hover:border-yellow-400 transition-colors">
                    <span className="font-mono text-yellow-400 text-xs pt-1 shrink-0">{String(j + 1).padStart(2, "0")}</span>
                    <span className="leading-snug">{s}</span>
                  </li>
                ))}
              </ol>
            </Section>
          )}

          {/* What to lean into vs differentiate */}
          <div className="grid sm:grid-cols-2 gap-4">
            {cluster.what_to_copy?.length > 0 && (
              <Section title="Safe to lean into" tone="emerald">
                <ul className="space-y-2 text-xs text-zinc-200 leading-relaxed">
                  {cluster.what_to_copy.map((t, j) => (
                    <li key={j} className="flex gap-2"><span className="text-emerald-400 shrink-0">✓</span><span>{t}</span></li>
                  ))}
                </ul>
              </Section>
            )}
            {cluster.what_to_differentiate?.length > 0 && (
              <Section title="Must differentiate" tone="amber">
                <ul className="space-y-2 text-xs text-zinc-200 leading-relaxed">
                  {cluster.what_to_differentiate.map((t, j) => (
                    <li key={j} className="flex gap-2"><span className="text-amber-400 shrink-0">✱</span><span>{t}</span></li>
                  ))}
                </ul>
              </Section>
            )}
          </div>

          {/* Realistic revenue projection */}
          {cluster.estimated_realistic_revenue && (
            <Section title="Realistic revenue projection (your channel)">
              <div className="grid grid-cols-3 gap-3 mb-2">
                <RevTile label="Month 3" value={cluster.estimated_realistic_revenue.month_3_usd} />
                <RevTile label="Month 6" value={cluster.estimated_realistic_revenue.month_6_usd} hi />
                <RevTile label="Month 12" value={cluster.estimated_realistic_revenue.month_12_usd} />
              </div>
              <p className="text-[11px] text-zinc-400 leading-relaxed">
                {cluster.estimated_realistic_revenue.reasoning}
              </p>
            </Section>
          )}

          {/* Risk */}
          {cluster.replication_risk && (
            <Section title="Replication risk" tone="warn">
              <p className="text-xs text-zinc-200 leading-relaxed flex gap-2">
                <span className="text-amber-400 shrink-0">⚠</span>
                <span>{cluster.replication_risk}</span>
              </p>
            </Section>
          )}
        </div>
      )}
    </div>
  );
}

function StageRow({ label, active, done }) {
  return (
    <div className="flex items-center gap-3 font-mono text-xs">
      <span className={`h-2 w-2 rounded-full ${
        done ? "bg-emerald-400" : active ? "bg-yellow-400 animate-pulse" : "bg-zinc-700"
      }`} />
      <span className={done ? "text-emerald-300" : active ? "text-yellow-400" : "text-zinc-500"}>
        {label}
      </span>
      {done && <span className="text-emerald-400">✓</span>}
    </div>
  );
}

function Section({ title, children, tone }) {
  const border =
    tone === "warn" ? "border-amber-500/30"
    : tone === "emerald" ? "border-emerald-500/30"
    : tone === "amber" ? "border-amber-500/30"
    : "border-zinc-800";
  return (
    <div className={`border ${border} p-4 rounded-sm bg-black`}>
      <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-yellow-400 font-semibold mb-3">
        {title}
      </div>
      {children}
    </div>
  );
}

function Stat({ label, value, small = false }) {
  return (
    <div className="border border-zinc-800 px-3 py-2 rounded-sm">
      <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">{label}</div>
      <div className={`text-white font-semibold ${small ? "text-xs leading-snug" : "font-display text-lg"}`}>
        {value}
      </div>
    </div>
  );
}

function RevTile({ label, value, hi = false }) {
  return (
    <div className={`border ${hi ? "border-yellow-400/40 bg-yellow-400/5" : "border-zinc-800"} p-3 rounded-sm`}>
      <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1">{label}</div>
      <div className={`font-display text-xl font-bold ${hi ? "text-yellow-400" : "text-emerald-300"}`}>
        ${nFmt(value)}<span className="text-zinc-500 text-xs font-normal"> / mo</span>
      </div>
    </div>
  );
}
